import { injectMutation } from '@tanstack/angular-query-experimental';
import { MutationOptions } from '@tanstack/query-core';
import { environment } from 'src/environments/environment';

export interface CustomInjectMutationArgs<
  TCoreFn extends (vars: TCoreVariables) => Promise<TCoreData>,
  TTransformer extends (args: TUserVariables) => TCoreVariables,
  TCoreData = Awaited<ReturnType<TCoreFn>>,
  TCoreError extends Error = Error,
  TCoreVariables = ReturnType<TTransformer>,
  TUserVariables = Parameters<TTransformer>,
  TContext = unknown,
> extends Omit<MutationOptions<TCoreData, TCoreError, TCoreVariables, TContext>, 'mutationFn'> {
  coreMutationFn: TCoreFn;
  transformer: TTransformer;
}

export function createWrappedMutation<
  TData,
  TError,
  TTransformer extends (args: TUserArgs) => TTransformedVars,
  TUserArgs,
  TTransformedVars,
  TContext = unknown,
>(
  baseMutation: ReturnType<typeof injectMutation<TData, TError, TTransformedVars, TContext>>,
  transformer: TTransformer
) {
  type UserVariablesFromTransformer = Parameters<TTransformer>[0];

  type BaseMutateOptions = Parameters<typeof baseMutation.mutate>[1];
  type BaseMutateAsyncOptions = Parameters<typeof baseMutation.mutateAsync>[1];

  return {
    ...baseMutation,
    mutate: (userVariables: UserVariablesFromTransformer, options?: BaseMutateOptions) => {
      const transformedVariables: TTransformedVars = transformer(userVariables);

      return baseMutation.mutate(transformedVariables, options);
    },
    mutateAsync: (
      userVariables: UserVariablesFromTransformer,
      options?: BaseMutateAsyncOptions
    ): Promise<TData> => {
      const transformedVariables: TTransformedVars = transformer(userVariables);

      return baseMutation.mutateAsync(transformedVariables, options);
    },
  };
}

export function customInjectMutation<
  ActualCoreFn extends (vars: DerivedCoreVariables) => Promise<DerivedCoreData>,
  ActualTransformer extends (args: DerivedUserVariables) => DerivedCoreVariables,
  DerivedCoreData = Awaited<ReturnType<ActualCoreFn>>,
  DerivedError extends Error = Error,
  DerivedCoreVariables = ReturnType<ActualTransformer>,
  DerivedUserVariables = Parameters<ActualTransformer>[0],
  DerivedContext = unknown,
>(
  args: CustomInjectMutationArgs<
    ActualCoreFn,
    ActualTransformer,
    DerivedCoreData,
    DerivedError,
    DerivedCoreVariables,
    DerivedUserVariables,
    DerivedContext
  >
) {
  const {
    coreMutationFn,
    mutationKey,
    onError: userProvidedOnError,
    transformer,
    ...standardMutationArgs
  } = args;

  const mutationFn = _createMutationFnForOriginalInject<DerivedCoreVariables, DerivedCoreData>(
    coreMutationFn,
    mutationKey
  );

  const combinedOnError = _createCombinedOnError<
    DerivedCoreVariables,
    DerivedContext,
    DerivedError
  >(mutationKey, userProvidedOnError);

  const baseMutation = injectMutation<
    DerivedCoreData,
    DerivedError,
    DerivedCoreVariables,
    DerivedContext
  >(() => ({
    ...standardMutationArgs,
    mutationFn,
    mutationKey,
    onError: combinedOnError,
  }));

  return createWrappedMutation<
    DerivedCoreData,
    DerivedError,
    ActualTransformer,
    DerivedUserVariables,
    DerivedCoreVariables,
    DerivedContext
  >(baseMutation, transformer);
}

function _createCombinedOnError<TTransformedVars, TContext, TError extends Error>(
  mutationKey: readonly unknown[] | undefined,
  userProvidedOnError?: (
    error: TError,
    variables: TTransformedVars,
    context: TContext | undefined
  ) => (Promise<unknown> | unknown) | undefined
): (error: TError, variables: TTransformedVars, context: TContext | undefined) => void {
  return (error: TError, variables: TTransformedVars, context: TContext | undefined): void => {
    const mutationKeyString = Array.isArray(mutationKey)
      ? mutationKey.join('/')
      : String(mutationKey);

    console.error(`Error in mutation for key ${mutationKeyString}:`, error);

    if (userProvidedOnError) {
      void userProvidedOnError(error, variables, context);
    }
  };
}

function _createMutationFnForOriginalInject<TTransformedVars, TDataInternal>(
  coreMutationFnParam: (vars: TTransformedVars) => Promise<TDataInternal>,
  mutationKey: readonly unknown[] | undefined
) {
  return (transformedVariables: TTransformedVars): Promise<TDataInternal> => {
    if (environment.usePlayground) {
      console.warn(
        `[Playground Mode] Skipping mutation for key ${String(mutationKey)}:`,
        transformedVariables
      );

      return new Promise<TDataInternal>((resolve) =>
        setTimeout(() => resolve(null as unknown as TDataInternal), 500)
      );
    }

    if (transformedVariables === null) {
      console.warn(
        `Skipping mutation for key ${String(mutationKey)} due to null transformed variables.`
      );

      return Promise.resolve(null as unknown as TDataInternal);
    }

    return coreMutationFnParam(transformedVariables);
  };
}
